import { ParsedBrand } from '../types/brand'

interface ExportOptions {
  includeAllColumns?: boolean
  selectedColumns?: string[]
  filename?: string
}

// Column definitions for export
export const EXPORT_COLUMNS = {
  brand: 'Brand Name',
  website: 'Website',
  amazon_presence: 'Amazon Presence',
  manufacturing_verification: 'Manufacturing Status',
  hq_location: 'HQ Location',
  mfg_locations: 'Manufacturing Locations',
  primary_products: 'Primary Products',
  clean_categories: 'Main Categories',
  product_categories: 'Detailed Categories',
  purchase_methods: 'Purchase Methods',
  founded_year: 'Founded Year',
  business_type: 'Business Type',
  certifications: 'Certifications',
  price_tier: 'Price Tier',
  consumer_summary: 'Brand Description',
  evidence_sources: 'Evidence Sources',
  confidence_score: 'Confidence Score',
  last_verified: 'Last Verified'
} as const

export const DEFAULT_EXPORT_COLUMNS = [
  'brand',
  'website',
  'manufacturing_verification',
  'hq_location',
  'clean_categories',
  'founded_year',
  'price_tier',
  'amazon_presence',
  'confidence_score'
] as const

function escapeCSVField(field: any): string {
  if (field === null || field === undefined) return ''
  
  let value = String(field)
  
  // Handle arrays (join with semicolons)
  if (Array.isArray(field)) {
    value = field.join('; ')
  }
  
  // Escape quotes and wrap in quotes if necessary
  if (value.includes('"') || value.includes(',') || value.includes('\n') || value.includes('\r')) {
    value = '"' + value.replace(/"/g, '""') + '"'
  }
  
  return value
}

function formatValueForExport(brand: ParsedBrand, column: string): any {
  const value = (brand as any)[column]
  
  switch (column) {
    case 'founded_year':
      return typeof value === 'number' ? value : 'Unknown'
    
    case 'manufacturing_verification':
    case 'amazon_presence':
    case 'price_tier':
      return value.replace(/_/g, ' ').replace(/\b\w/g, (l: string) => l.toUpperCase())
    
    case 'mfg_locations':
    case 'clean_categories':
    case 'product_categories':
    case 'certifications':
      return Array.isArray(value) ? value : []
    
    case 'last_verified':
      return value ? new Date(value).toLocaleString() : 'Unknown'
    
    case 'website':
      return value !== 'unknown' ? value : ''
    
    default:
      return value !== 'unknown' ? value : ''
  }
}

export function exportBrandsToCSV(brands: ParsedBrand[], options: ExportOptions = {}) {
  const {
    includeAllColumns = false,
    selectedColumns = DEFAULT_EXPORT_COLUMNS,
    filename = `brandscope-export-${new Date().toISOString().split('T')[0]}.csv`
  } = options

  // Determine which columns to export
  const columnsToExport = includeAllColumns 
    ? Object.keys(EXPORT_COLUMNS)
    : selectedColumns

  // Create header row
  const headers = columnsToExport.map(col => 
    EXPORT_COLUMNS[col as keyof typeof EXPORT_COLUMNS] || col
  )

  // Create data rows
  const rows = brands.map(brand => 
    columnsToExport.map(column => {
      const value = formatValueForExport(brand, column)
      return escapeCSVField(value)
    })
  )

  // Combine headers and data
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(','))
  ].join('\n')

  // Create and trigger download
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
  const link = document.createElement('a')
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  return {
    filename,
    recordCount: brands.length,
    columnCount: columnsToExport.length
  }
}

export function generateExportSummary(brands: ParsedBrand[], totalBrands: number) {
  const stats = {
    totalExported: brands.length,
    totalAvailable: totalBrands,
    filterPercentage: totalBrands > 0 ? (brands.length / totalBrands * 100).toFixed(1) : '0',
    
    // Manufacturing breakdown
    confirmedUSA: brands.filter(b => b.manufacturing_verification === 'confirmed_usa').length,
    likelyUSA: brands.filter(b => b.manufacturing_verification === 'likely_usa').length,
    
    // Amazon presence
    onAmazon: brands.filter(b => b.amazon_presence !== 'none' && b.amazon_presence !== 'unknown').length,
    
    // Data quality
    highConfidence: brands.filter(b => b.confidence_score >= 8).length,
    
    // Founded years
    foundedYears: brands
      .map(b => b.founded_year)
      .filter((year): year is number => typeof year === 'number'),
  }

  const avgConfidence = brands.length > 0 
    ? (brands.reduce((sum, b) => sum + b.confidence_score, 0) / brands.length).toFixed(1)
    : '0'

  return {
    ...stats,
    avgConfidence,
    yearRange: stats.foundedYears.length > 0 
      ? `${Math.min(...stats.foundedYears)}-${Math.max(...stats.foundedYears)}`
      : 'N/A'
  }
}